import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Card, Select, Button, Typography, Space } from 'antd'
import { SwapOutlined } from '@ant-design/icons'
import { toast } from 'react-toastify'
import { useAuthStore } from '@/stores/authStore'
import { listarTodosParceiros } from '@/api/parceiros'

const { Title, Text } = Typography

// ─── Entry component ─────────────────────────────────────────────────────────

export function SelecionarParceiro() {
  return (
    <div className="min-h-screen bg-[#f3f4f5] flex items-center justify-center p-4">
      <ParceiroSelectorCard />
    </div>
  )
}

// ─── Helper components ────────────────────────────────────────────────────────

function ParceiroSelectorCard() {
  return (
    <Card className="w-full max-w-md shadow-sm">
      <Space direction="vertical" size="large" className="w-full">
        <CardHeader />
        <ParceiroSelectForm />
      </Space>
    </Card>
  )
}

function CardHeader() {
  const usuario = useAuthStore((s) => s.usuario)
  return (
    <div>
      <Title level={4} className="!mb-1">Selecionar Parceiro</Title>
      <Text type="secondary">
        Olá, {usuario?.nome}. Escolha um parceiro para visualizar seus dados.
      </Text>
    </div>
  )
}

function ParceiroSelectForm() {
  const [selectedParceiroId, setSelectedParceiroId] = useState<number | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const selecionarParceiro = useAuthStore((s) => s.selecionarParceiro)
  const { options, loadingParceiros } = useOpcoesDeParceiro()
  const navigate = useNavigate()

  async function handleConfirmar() {
    if (!selectedParceiroId) return
    setSubmitting(true)
    try {
      await selecionarParceiro(selectedParceiroId)
      navigate('/dashboard', { replace: true })
    } catch {
      toast.error('Erro ao selecionar parceiro')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Space direction="vertical" size="middle" className="w-full">
      <Select
        showSearch
        placeholder="Buscar parceiro por nome ou CNPJ..."
        loading={loadingParceiros}
        options={options}
        value={selectedParceiroId}
        onChange={setSelectedParceiroId}
        filterOption={(input, option) =>
          (option?.search ?? '').includes(input.toLowerCase())
        }
        className="w-full"
        size="large"
      />
      <Button
        type="primary"
        icon={<SwapOutlined />}
        size="large"
        block
        disabled={!selectedParceiroId}
        loading={submitting}
        onClick={handleConfirmar}
      >
        Acessar Parceiro
      </Button>
    </Space>
  )
}

// ─── Helper hooks ─────────────────────────────────────────────────────────────

/**
 * Que parceiros esta pessoa pode acessar.
 *
 * Administrador da New Driver escolhe entre TODOS — e por isso paga a busca no servidor. Usuário
 * comum escolhe apenas entre os seus vínculos, que já vieram na sessão: pedir a lista completa
 * seria bater numa rota que ele não tem permissão de chamar.
 */
function useOpcoesDeParceiro() {
  const isAdministrador = useAuthStore((s) => s.isAdministrador)
  const parceirosVinculados = useAuthStore((s) => s.parceiros)
  const [todosParceiros, setTodosParceiros] = useState<
    Array<{ id: number; cpfCnpj: string; nome: string }>
  >([])
  const [loadingParceiros, setLoadingParceiros] = useState(isAdministrador)

  useEffect(() => {
    if (!isAdministrador) return
    setLoadingParceiros(true)
    listarTodosParceiros()
      .then(setTodosParceiros)
      .catch(() => toast.error('Erro ao carregar lista de parceiros'))
      .finally(() => setLoadingParceiros(false))
  }, [isAdministrador])

  const options = isAdministrador
    ? todosParceiros.map((parceiro) => ({
        value: parceiro.id,
        label: `${parceiro.nome} — ${parceiro.cpfCnpj}`,
        search: `${parceiro.nome} ${parceiro.cpfCnpj}`.toLowerCase(),
      }))
    : parceirosVinculados.map((parceiro) => ({
        value: parceiro.id,
        label: parceiro.nome,
        search: parceiro.nome.toLowerCase(),
      }))

  return { options, loadingParceiros }
}
